import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import useScrollLock from '../../hooks/useScrollLock';
import './Salon.css';

const todayString = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};

const calculateHoursWorked = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const [startHours, startMinutes] = startTime.split(':').map(Number);
  const [endHours, endMinutes] = endTime.split(':').map(Number);
  const diffMinutes = (endHours * 60 + endMinutes) - (startHours * 60 + startMinutes);
  return Math.round((diffMinutes / 60) * 100) / 100;
};

// Saturday defaults to shorter working hours; every other day (including Sunday, which is
// still loggable even though it's marked as non-working) defaults to the regular hours.
const getDefaultTimesForDate = (dateStr) => {
  const [year, month, day] = (dateStr || todayString()).split('-').map(Number);
  const dayOfWeek = new Date(year, month - 1, day).getDay();
  return dayOfWeek === 6 ? { start_time: '10:00', end_time: '15:00' } : { start_time: '12:00', end_time: '20:00' };
};

const LogHoursModal = ({ isOpen, onClose, salonId, existingLog, initialDate, onSaved }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ work_date: todayString(), ...getDefaultTimesForDate(todayString()), note: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useScrollLock(isOpen);

  useEffect(() => {
    if (existingLog) {
      setFormData({
        work_date: existingLog.work_date,
        start_time: existingLog.start_time,
        end_time: existingLog.end_time,
        note: existingLog.note || ''
      });
    } else {
      const workDate = initialDate || todayString();
      setFormData({ work_date: workDate, ...getDefaultTimesForDate(workDate), note: '' });
    }
    setError('');
  }, [existingLog, initialDate, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const hoursWorked = calculateHoursWorked(formData.start_time, formData.end_time);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    if (!formData.work_date) {
      setError(t('timeOff.hours.dateRequired'));
      return;
    }
    if (!formData.start_time || !formData.end_time) {
      setError(t('timeOff.hours.timesRequired'));
      return;
    }
    if (hoursWorked <= 0) {
      setError(t('timeOff.hours.invalidTimeRange'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const payload = { work_date: formData.work_date, start_time: formData.start_time, end_time: formData.end_time, note: formData.note };

      if (existingLog) {
        await axios.put(`/api/salons/${salonId}/time-logs/${existingLog.id}`, payload, config);
      } else {
        await axios.post(`/api/salons/${salonId}/time-logs`, payload, config);
      }

      setLoading(false);
      onSaved();
    } catch (err) {
      console.error('Error saving time log:', err);
      if (err.response?.status === 409) {
        setError(t('timeOff.hours.duplicateDate'));
      } else {
        setError(err.response?.data?.error || t('timeOff.hours.failedToSave'));
      }
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{existingLog ? t('timeOff.hours.editEntry') : t('timeOff.hours.logHours')}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <form onSubmit={handleSubmit} id="log-hours-form" noValidate>
            <div className="form-group">
              <label htmlFor="work_date">{t('timeOff.hours.date')}</label>
              <input
                type="date"
                id="work_date"
                name="work_date"
                value={formData.work_date}
                onChange={handleChange}
                disabled
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="start_time">{t('timeOff.hours.startTime')} *</label>
                <input
                  type="time"
                  id="start_time"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="end_time">{t('timeOff.hours.endTime')} *</label>
                <input
                  type="time"
                  id="end_time"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleChange}
                />
              </div>
            </div>

            {hoursWorked > 0 && (
              <div className="time-off-summary">
                {t('timeOff.hours.hoursWorked')}: <strong>{hoursWorked}h</strong>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="note">{t('timeOff.hours.note')}</label>
              <textarea
                id="note"
                name="note"
                value={formData.note}
                onChange={handleChange}
                maxLength={500}
                rows={3}
              />
            </div>

            {error && <div className="error-message">{error}</div>}
          </form>
        </div>

        <div className="modal-footer">
          <button type="button" className="salon-button secondary" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </button>
          <button type="submit" form="log-hours-form" className="salon-button primary" disabled={loading}>
            {loading ? `${t('common.save')}...` : t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default LogHoursModal;
